import React from 'react'
import {
  DateField,
  FunctionField,
  ReferenceField,
  Show,
  ShowProps,
  Tab,
  TabbedShowLayout,
  TextField,
  TitleProps,
  useTranslate,
} from 'react-admin'
import { SystemLogDto } from '../../../core/dto/system-notifications/system-log.dto'
import { SystemLogTypes } from '../../../core/enum/SystemLogTypes'
import { DoorTabs } from '../../devices/doors/DoorShow'
import { EngineTabs } from '../../devices/engines/EngineShow'
import { ResourceName } from '../../../core/ResourceName'
import { DynamicCounterTabs } from '../../administration/dynamic-counters/DynamicCounterShow'
import { OfflineServerTabs } from '../../devices/offline-servers/OfflineServerShow'
import { PassageTabs } from '../../devices/passages/PassageShow'
import { TerminalTabs } from '../../devices/terminals/TerminalShow'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import { Authority } from '../../../core/auth/Authority'
import { NotificationCategoryDto } from '../../../core/dto/system-notifications/notification-category.dto'
import { BarrierTabs } from '../../devices/barriers/BarrierShow'
import { IksorisParkingTicketCashMachineTabs } from '../../devices/ticket-machines/iksoris-parking-ticket-cash-machines/IksorisParkingTicketCashMachineShow'
import { IksorisParkingTicketCashlessMachineTabs } from '../../devices/ticket-machines/iksoris-parking-ticket-cashless-machines/IksorisParkingTicketCashlessMachineShow'
import { SelfPOSAccessTicketCashlessMachineTabs } from '../../devices/ticket-machines/selfpos-access-ticket-cashless-machines/SelfPOSAccessTicketCashlessMachineShow'
import { CameraTabs } from '../../devices/camera/cameras/CameraShow'
import { CameraServerTabs } from '../../devices/camera/camera-servers/CameraServerShow'

interface SystemLogsTitleProps extends TitleProps {
  record?: SystemLogDto
}

const ShowTitle = (data: SystemLogsTitleProps) => {
  const { record: tag } = data
  const translate = useTranslate()
  return (
    <span>
      {translate('resources.system-logs.titles.show')}: {tag?.id}
    </span>
  )
}

const SystemLogView = ({ ...props }) => {
  const {
    record: { createdAt, jsonSnapshot, type },
  } = props
  const snapshot = JSON.parse(jsonSnapshot)

  switch (type) {
    case SystemLogTypes.DOOR_LOG: {
      return (
        <DoorTabs
          record={snapshot}
          resource={ResourceName.DOORS}
          simpleView
          createdAt={createdAt}
        />
      )
    }
    case SystemLogTypes.DYNAMIC_COUNTER_LOG: {
      return (
        <DynamicCounterTabs
          record={snapshot}
          resource={ResourceName.DYNAMIC_COUNTERS}
          simpleView
        />
      )
    }
    case SystemLogTypes.ENGINE_LOG: {
      return (
        <EngineTabs
          record={snapshot}
          resource={ResourceName.ENGINES}
          simpleView
          createdAt={createdAt}
        />
      )
    }
    case SystemLogTypes.OFFLINE_SERVER_LOG: {
      return (
        <OfflineServerTabs
          record={snapshot}
          resource={ResourceName.OFFLINE_SERVERS}
          simpleView
          createdAt={createdAt}
        />
      )
    }
    case SystemLogTypes.PASSAGE_LOG: {
      return (
        <PassageTabs
          record={snapshot}
          resource={ResourceName.PASSAGES}
          simpleView
          createdAt={createdAt}
        />
      )
    }
    case SystemLogTypes.TERMINAL_LOG: {
      return (
        <TerminalTabs
          record={snapshot}
          resource={ResourceName.TERMINALS}
          simpleView
          createdAt={createdAt}
        />
      )
    }
    case SystemLogTypes.BARRIER_LOG: {
      return (
        <BarrierTabs
          record={snapshot}
          resource={ResourceName.TERMINALS}
          simpleView
          createdAt={createdAt}
        />
      )
    }
    case SystemLogTypes.IKSORIS_PARKING_TICKET_CASH_MACHINE_LOG: {
      return (
        <IksorisParkingTicketCashMachineTabs
          record={snapshot}
          resource={ResourceName.IKSORIS_PARKING_TICKET_CASH_MACHINES}
          simpleView
          createdAt={createdAt}
        />
      )
    }
    case SystemLogTypes.IKSORIS_PARKING_TICKET_CASHLESS_MACHINE_LOG: {
      return (
        <IksorisParkingTicketCashlessMachineTabs
          record={snapshot}
          resource={ResourceName.IKSORIS_PARKING_TICKET_CASHLESS_MACHINES}
          simpleView
          createdAt={createdAt}
        />
      )
    }
    case SystemLogTypes.SELF_POS_ACCESS_TICKET_CASHLESS_MACHINE_LOG: {
      return (
        <SelfPOSAccessTicketCashlessMachineTabs
          record={snapshot}
          resource={ResourceName.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES}
          simpleView
          createdAt={createdAt}
        />
      )
    }
    case SystemLogTypes.CAMERA_LOG: {
      return (
        <CameraTabs
          record={snapshot}
          resource={ResourceName.CAMERAS}
          simpleView
          createdAt={createdAt}
        />
      )
    }
    case SystemLogTypes.CAMERA_SERVER_LOG: {
      return (
        <CameraServerTabs
          record={snapshot}
          resource={ResourceName.CAMERA_SERVERS}
          simpleView
          createdAt={createdAt}
        />
      )
    }
    default: {
      return <div />
    }
  }
}

const SystemLogTabs = ({ ...props }) => {
  const { record: log } = props
  const hasAuthority = useHasAuthority()
  const translate = useTranslate()

  return (
    <TabbedShowLayout {...props}>
      <Tab label="resources.system-logs.tabs.log">
        <TextField source="id" label="ID" />
        <FunctionField<SystemLogDto>
          source="type"
          render={(record?: SystemLogDto) =>
            translate(`resources.enums.SystemLogTypes.${record?.type}`)
          }
        />
        {log?.description && <TextField source="description" />}
        {hasAuthority(Authority.VIEW_NOTIFICATION_TAGS) && (
          <ReferenceField
            source="categoryId"
            reference={ResourceName.NOTIFICATION_CATEGORIES}
            link={false}
          >
            <FunctionField<NotificationCategoryDto>
              source="name"
              render={(category?: NotificationCategoryDto) =>
                `${translate(
                  `resources.enums.NotificationCategoryNames.${category?.name}`,
                )} (${category?.defaultDescription})`
              }
            />
          </ReferenceField>
        )}
        {log?.doorId && hasAuthority(Authority.VIEW_DOORS) && (
          <ReferenceField
            source="doorId"
            reference={ResourceName.DOORS}
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
        )}
        {log?.dynamicCounterId &&
          hasAuthority(Authority.VIEW_DYNAMIC_COUNTERS) && (
            <ReferenceField
              source="dynamicCounterId"
              reference={ResourceName.DYNAMIC_COUNTERS}
              link="show"
            >
              <TextField source="name" />
            </ReferenceField>
          )}
        {log?.engineId && hasAuthority(Authority.VIEW_ENGINES) && (
          <ReferenceField
            source="engineId"
            reference={ResourceName.ENGINES}
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
        )}
        {log?.offlineServerId && hasAuthority(Authority.VIEW_OFFLINE_SERVERS) && (
          <ReferenceField
            source="offlineServerId"
            reference={ResourceName.OFFLINE_SERVERS}
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
        )}
        {log?.passageId && hasAuthority(Authority.VIEW_PASSAGES) && (
          <ReferenceField
            source="passageId"
            reference={ResourceName.PASSAGES}
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
        )}
        {log?.terminalId && hasAuthority(Authority.VIEW_TERMINALS) && (
          <ReferenceField
            source="terminalId"
            reference={ResourceName.TERMINALS}
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
        )}
        {log?.createdAt && <DateField source="createdAt" showTime />}
      </Tab>
      <Tab path="state" label="resources.system-logs.tabs.state">
        <SystemLogView record={log} />
      </Tab>
    </TabbedShowLayout>
  )
}

export const SystemLogShow = (props: ShowProps) => (
  <Show {...props} actions={false} title={<ShowTitle />}>
    <SystemLogTabs />
  </Show>
)
